'use client';

import { useEffect, useState } from 'react';
import styles from './style.module.scss';

const CONSENT_COOKIE = 'moove_gdpr_popup';
const LEGACY_CONSENT_COOKIE = 'seen_cookie_consent';
const CONSENT_MAX_AGE = 60 * 60 * 24 * 365;

function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const value = document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith(`${name}=`))
    ?.slice(name.length + 1);

  if (!value) return null;

  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function parseMooveConsent(value) {
  if (!value) return null;
  try {
    const saved = JSON.parse(value);
    if (saved.strict !== '1' && saved.strict !== 1) return null;
    return {
      necessary: true,
      analytics: saved.thirdparty === '1' || saved.thirdparty === 1 || saved.performance === '1' || saved.performance === 1,
      marketing: saved.advanced === '1' || saved.advanced === 1,
      updatedAt: Date.now(),
    };
  } catch {
    return null;
  }
}

function getCookieDomain(cmsHostname) {
  const appLabels = window.location.hostname.split('.').reverse();
  const cmsLabels = (cmsHostname || '').split('.').reverse();
  const shared = [];
  while (appLabels[shared.length] && appLabels[shared.length] === cmsLabels[shared.length]) {
    shared.push(appLabels[shared.length]);
  }
  return shared.length >= 3 ? `; Domain=.${shared.reverse().join('.')}` : '';
}

function writeConsentCookie(consent, cmsHostname) {
  const mooveConsent = {
    strict: '1',
    thirdparty: consent.analytics ? '1' : '0',
    advanced: consent.marketing ? '1' : '0',
    performance: '0',
    preference: '0',
  };
  document.cookie = `${CONSENT_COOKIE}=${encodeURIComponent(JSON.stringify(mooveConsent))}; Max-Age=${CONSENT_MAX_AGE}; Path=/; SameSite=Lax${getCookieDomain(cmsHostname)}${window.location.protocol === 'https:' ? '; Secure' : ''}`;

  window.gdpr_consent__strict = true;
  window.gdpr_consent__thirdparty = consent.analytics;
  window.gdpr_consent__advanced = consent.marketing;
  window.gdpr_consent__performance = false;
  window.gdpr_consent__preference = false;
  window.gdpr_consent__cookies = mooveConsent;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'cookie_consent_update', ...window.gdpr_consent__cookies });
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: consent.analytics ? 'granted' : 'denied',
      ad_storage: consent.marketing ? 'granted' : 'denied',
      ad_user_data: consent.marketing ? 'granted' : 'denied',
      ad_personalization: consent.marketing ? 'granted' : 'denied',
    });
  }
  if (typeof window.wp_set_consent === 'function') {
    window.wp_set_consent('functional', 'allow');
    window.wp_set_consent('statistics', consent.analytics ? 'allow' : 'deny');
    window.wp_set_consent('marketing', consent.marketing ? 'allow' : 'deny');
    window.wp_set_consent('preferences', 'deny');
  }
  window.dispatchEvent(new CustomEvent('cookie_consent_update', { detail: mooveConsent }));
}

function appendCode(code, id, target = document.body) {
  if (!code || document.getElementById(id)) return;

  const template = document.createElement('template');
  template.innerHTML = code.trim();
  const nodes = [...template.content.childNodes];

  if (!nodes.some((node) => node.nodeName === 'SCRIPT')) {
    const script = document.createElement('script');
    script.id = id;
    script.text = code;
    target.appendChild(script);
    return;
  }

  nodes.forEach((node, index) => {
    if (node.nodeName === 'SCRIPT') {
      const script = document.createElement('script');
      [...node.attributes].forEach(({ name, value }) => script.setAttribute(name, value));
      script.id = index === 0 ? id : `${id}-${index}`;
      script.text = node.textContent;
      target.appendChild(script);
    } else if (node.nodeType === Node.ELEMENT_NODE && node.nodeName !== 'NOSCRIPT') {
      target.appendChild(node.cloneNode(true));
    }
  });
}

function scriptCategory(script) {
  const title = script.title || '';
  if (/marketing|advert|facebook|meta pixel|remarket/i.test(title)) return 'marketing';
  if (/analytics|google analytics|matomo|plausible/i.test(title)) return 'analytics';
  return 'both';
}

export default function CookieConsent({ gtmScripthead, headScripts, bodyScripts, footerScripts, cmsHostname }) {
  const [consent, setConsent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    let saved = parseMooveConsent(readCookie(CONSENT_COOKIE));
    if (!saved) {
      const legacyValue = readCookie(LEGACY_CONSENT_COOKIE);
      if (legacyValue) {
        try {
          const legacy = JSON.parse(legacyValue);
          saved = { necessary: true, analytics: Boolean(legacy.analytics), marketing: Boolean(legacy.marketing), updatedAt: Date.now() };
          writeConsentCookie(saved, cmsHostname);
        } catch {
          saved = null;
        }
      }
    }
    setConsent(saved);
    setAnalytics(Boolean(saved?.analytics));
    setMarketing(Boolean(saved?.marketing));
    setVisible(!saved);
  }, [cmsHostname]);

  useEffect(() => {
    if (!consent) return;
    if (consent.analytics) {
      if (consent.marketing) appendCode(gtmScripthead, 'seen-gtm-head', document.head);
      [
        { scripts: headScripts, target: document.head, place: 'head' },
        { scripts: bodyScripts, target: document.body, place: 'body' },
        { scripts: footerScripts, target: document.body, place: 'footer' },
      ].forEach(({ scripts, target, place }) =>
        (scripts || [])
          .filter((script) => !script.enabled && (scriptCategory(script) === 'analytics' || (scriptCategory(script) === 'both' && consent.marketing)))
          .forEach((script, index) => appendCode(script.code, `seen-analytics-${place}-${index}`, target))
      );
    }
    if (consent.marketing) {
      [
        { scripts: headScripts, target: document.head, place: 'head' },
        { scripts: bodyScripts, target: document.body, place: 'body' },
        { scripts: footerScripts, target: document.body, place: 'footer' },
      ].forEach(({ scripts, target, place }) =>
        (scripts || [])
          .filter((script) => !script.enabled && scriptCategory(script) === 'marketing')
          .forEach((script, index) => appendCode(script.code, `seen-marketing-${place}-${index}`, target))
      );
    }
  }, [consent, gtmScripthead, headScripts, bodyScripts, footerScripts]);

  function saveConsent(next) {
    const changed = consent && (consent.analytics !== next.analytics || consent.marketing !== next.marketing);
    writeConsentCookie(next, cmsHostname);
    setConsent(next);
    setVisible(false);
    setSettingsOpen(false);
    if (changed) window.location.reload();
  }

  useEffect(() => {
    const openSettings = () => {
      setAnalytics(Boolean(consent?.analytics));
      setMarketing(Boolean(consent?.marketing));
      setSettingsOpen(true);
      setVisible(true);
    };
    window.addEventListener('seen:cookie-settings', openSettings);
    return () => window.removeEventListener('seen:cookie-settings', openSettings);
  }, [consent]);

  return (
    <>
      {!visible && <button className={styles.settingsLink} onClick={() => window.dispatchEvent(new Event('seen:cookie-settings'))}>Cookie settings</button>}
      {visible && (
        <div className={styles.backdrop} role="presentation">
          <section className={styles.banner} role="dialog" aria-modal="true" aria-labelledby="cookie-title">
            <div>
              <p className={styles.eyebrow}>YOUR PRIVACY</p>
              <h2 id="cookie-title">Cookies &amp; privacy</h2>
              <p className={styles.copy}>
                We use necessary cookies to make this site work. With your permission, we also use analytics
                and marketing cookies. You can change your choice at any time in Cookie settings.
              </p>
              {settingsOpen && (
                <div className={styles.preferences}>
                  <label><input type="checkbox" checked disabled /> Necessary <span>Always on</span></label>
                  <label><input type="checkbox" checked={analytics} onChange={(event) => setAnalytics(event.target.checked)} /> Analytics</label>
                  <label><input type="checkbox" checked={marketing} onChange={(event) => setMarketing(event.target.checked)} /> Marketing</label>
                </div>
              )}
              <a className={styles.policy} href="/privacy-policy">Read our Privacy Policy</a>
            </div>
            <div className={styles.actions}>
              {settingsOpen ? (
                <button className={styles.primary} onClick={() => saveConsent({ necessary: true, analytics, marketing, updatedAt: Date.now() })}>Save choices</button>
              ) : (
                <>
                  <button className={styles.secondary} onClick={() => saveConsent({ necessary: true, analytics: false, marketing: false, updatedAt: Date.now() })}>Reject optional</button>
                  <button className={styles.primary} onClick={() => saveConsent({ necessary: true, analytics: true, marketing: true, updatedAt: Date.now() })}>Accept all</button>
                </>
              )}
              {!settingsOpen && <button className={styles.textButton} onClick={() => setSettingsOpen(true)}>Settings</button>}
            </div>
          </section>
        </div>
      )}
    </>
  );
}
